import React, { useState, useEffect, useContext, useRef } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import API from '../../services/API'
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
  Typography,
} from '@mui/material'
import { GlobalContext, TenantSettingsContext, UserContext } from '../../App'
import { HandshakeRounded } from '@mui/icons-material'

const EmployeeSelector = ({
  valueType = 'user',
  value,
  defaultUserID,
  setSelfAsDefault,
  onChange,
  label,
  multiple = false,
  noPhoto,
  required,
  disabled,
  helperText,
  freeSolo,
  preventSelectingSelf,
  // User ids to leave out of the options, e.g. the administering clinician
  // when picking a controlled-drug witness
  excludeUserIds,
  fullWidth,
  size,
  team,
  shrinkLabel,
  includeSubcontractor,
  subcontractorOnly,
  excludeBannedFromEvents,
  error,
  additionalSelectors,
  ...props
}) => {
  const [employeeOptions, setEmployeeOptions] = useState([])
  const [inputValue, setInputValue] = useState('')
  const [loading, setLoading] = useState(false)
  // Selection of an inactive subcontractor held back until the user resolves
  // the reactivation dialog: { option, newValue, reason, details }
  const [inactiveSelection, setInactiveSelection] = useState(null)
  const [reactivating, setReactivating] = useState(false)

  const { user } = useContext(UserContext)
  const { setToast } = useContext(GlobalContext)
  const { orgConfig } = useContext(TenantSettingsContext)

  // Stable string form so the options effect doesn't refire on every render
  // from a fresh array literal
  const excludeKey = (excludeUserIds || [])
    .filter(Boolean)
    .map(String)
    .join(',')

  const canManageSubcontractors =
    !!user?.security?.isSiteAdmin ||
    !!user?.employee?.teams?.includes('Personnel')
  const accessWindowDays =
    orgConfig?.orgSettings?.subcontractorSettings?.accessWindowDays ?? 7
  const accessWindowLabel =
    accessWindowDays === 0
      ? 'indefinite access'
      : `${accessWindowDays} day${accessWindowDays === 1 ? '' : 's'}`

  const hasSetDefault = useRef(false)

  const getAssignmentId = (option) => {
    if (!option || typeof option === 'string') return ''

    return (
      option.subcontractor?._id ||
      option.employee?.subcontractor?._id ||
      option.employee?._id ||
      (typeof option.employee === 'string' ? option.employee : '') ||
      option._id ||
      ''
    )
  }

  const isEmptyValue = multiple
    ? !Array.isArray(value) || value.length === 0
    : !value
  const hasSubcontractorOptions =
    includeSubcontractor &&
    (employeeOptions.some((option) => !!option?.isSubcontractor) ||
      (multiple
        ? (value ?? []).some(
            (option) =>
              !!option?.isSubcontractor ||
              !!option?.subcontractor ||
              !!option?.employee?.subcontractor,
          )
        : !!value?.isSubcontractor ||
          !!value?.subcontractor ||
          !!value?.employee?.subcontractor))
  const inputLabel =
    includeSubcontractor && label === 'Assigned staff'
      ? hasSubcontractorOptions
        ? 'Assigned staff or subcontractor'
        : 'Assigned staff'
      : label

  useEffect(() => {
    if (
      setSelfAsDefault &&
      !hasSetDefault.current &&
      isEmptyValue &&
      !inputValue &&
      user?._id
    ) {
      hasSetDefault.current = true
      setLoading(true)
      API.get('/employee-selector', {
        params: {
          noPhoto: noPhoto ?? false,
          userID: user._id,
        },
      }).then((res) => {
        const selected = res.data

        if (valueType === 'employee') {
          const employeeValue = selected?.employee || null
          onChange(null, multiple ? [employeeValue] : employeeValue)
        } else {
          onChange(null, multiple ? [selected] : selected)
        }
      })
    }
    if (inputValue.trim().length < 2) {
      setEmployeeOptions([])
      setLoading(false)
      return
    }

    setLoading(true)

    let active = true

    const timeout = setTimeout(async () => {
      try {
        setLoading(true)

        const res = await API.get('/employee-selector', {
          params: {
            noPhoto: noPhoto ?? false,
            searchValue: inputValue.trim(),
            additionalSelectors,
            // Comma-joined: the server's hpp middleware collapses repeated
            // query params to the last value, so arrays must not be sent raw
            team: Array.isArray(team) ? team.join(',') : team,
            includeSubcontractor,
            subcontractorOnly,
            excludeBannedFromEvents,
          },
        })
        if (!active) return

        let results = Array.isArray(res.data) ? res.data : []

        if (preventSelectingSelf && user?._id) {
          results = results.filter((emp) => emp?._id !== user._id)
        }
        if (excludeKey) {
          const excluded = excludeKey.split(',')
          results = results.filter(
            (emp) => !excluded.includes(String(emp?._id)),
          )
        }

        setEmployeeOptions(results)
      } catch (err) {
        if (active) {
          console.error(err)
          setEmployeeOptions([])
        }
      } finally {
        if (active) setLoading(false)
      }
    }, 400)

    return () => {
      active = false
      clearTimeout(timeout)
    }
  }, [
    inputValue,
    noPhoto,
    team,
    includeSubcontractor,
    subcontractorOnly,
    excludeBannedFromEvents,
    preventSelectingSelf,
    excludeKey,
    user?._id,
  ])

  const getEmployeeFromOption = (option) => {
    if (!option) return null
    return valueType === 'employee' ? option.employee : option
  }

  const getOptionDisplayParts = (option) => {
    if (!option || typeof option === 'string') {
      return {
        firstName: typeof option === 'string' ? option : '',
        lastName: '',
        clinicalRole: '',
        isSubcontractor: false,
        photograph: null,
      }
    }

    const firstName =
      option.firstName ||
      option.userAccount?.firstName ||
      option.subcontractor?.userAccount?.firstName ||
      option.employee?.subcontractor?.userAccount?.firstName ||
      option.employee?.userAccount?.firstName ||
      ''

    const lastName =
      option.lastName ||
      option.userAccount?.lastName ||
      option.subcontractor?.userAccount?.lastName ||
      option.employee?.subcontractor?.userAccount?.lastName ||
      option.employee?.userAccount?.lastName ||
      ''

    const isSubcontractor =
      !!option.isSubcontractor ||
      !!option.employee?.isSubcontractor ||
      (!!option.subcontractor && !option.employee) ||
      !!option.employee?.subcontractor ||
      option.assignmentType === 'subcontractor'

    const clinicalRole =
      option.subcontractor?.clinicalRole ||
      option.employee?.subcontractor?.clinicalRole ||
      option.employee?.clinicalRole ||
      option.clinicalRole ||
      option.employee?.clinicalRole ||
      ''

    const photograph =
      option.photograph ||
      option.userAccount?.photograph ||
      option.subcontractor?.userAccount?.photograph ||
      option.employee?.subcontractor?.userAccount?.photograph ||
      option.employee?.userAccount?.photograph

    return { firstName, lastName, clinicalRole, isSubcontractor, photograph }
  }

  const getOptionDisplayLabel = (option) => {
    if (typeof option === 'string') return option
    const { firstName, lastName, clinicalRole } = getOptionDisplayParts(option)
    const name = `${firstName} ${lastName}`.trim()

    return `${name}${clinicalRole ? ` (${clinicalRole})` : ''}`.trim()
  }

  const SubcontractorIcon = ({ option }) => {
    const { isSubcontractor } = getOptionDisplayParts(option)

    if (!isSubcontractor) return null

    return (
      <Tooltip title="Subcontractor">
        <HandshakeRounded
          color="contrast"
          fontSize="small"
          aria-label="Subcontractor"
          sx={{ cursor: 'pointer' }}
        />
      </Tooltip>
    )
  }

  const SubcontractorStatusChip = ({ option }) => {
    // Only server search results carry the subcontractor doc + accessActive
    if (!option?.isSubcontractor || !option.subcontractor?.status) return null

    if (option.subcontractor.status === 'Pending Verification') {
      return <Chip size="small" color="warning" label="Pending" />
    }

    return option.accessActive ? (
      <Chip size="small" color="success" label="Active" />
    ) : (
      <Chip size="small" color="error" label="Inactive" />
    )
  }

  // Pending Verification subcontractors stay selectable (matching previous
  // behaviour) - reactivation cannot make them Active until they verify.
  const isInactiveSubcontractor = (option) =>
    !!option?.isSubcontractor &&
    !!option.subcontractor?.status &&
    option.subcontractor.status !== 'Pending Verification' &&
    !option.accessActive

  const normaliseValueForAutocomplete = (value) => {
    if (!value) return multiple ? [] : null

    if (multiple) {
      return value.map((v) => {
        // If parent stores employees, find matching user option
        if (valueType === 'employee') {
          return (
            employeeOptions.find(
              (opt) => getAssignmentId(opt) === getAssignmentId(v),
            ) || {
              employee: v,
              firstName: v.userAccount?.firstName || '',
              lastName: v.userAccount?.lastName || '',
              photograph: v.userAccount?.photograph,
            }
          )
        }

        return v
      })
    }

    if (valueType === 'employee') {
      return (
        employeeOptions.find(
          (opt) => getAssignmentId(opt) === getAssignmentId(value),
        ) || {
          employee: value,
          firstName: value.userAccount?.firstName || '',
          lastName: value.userAccount?.lastName || '',
          photograph: value.userAccount?.photograph,
        }
      )
    }

    return value
  }

  const getOptionsWithSelectedValues = (options, selectedValue) => {
    const selected = multiple
      ? Array.isArray(selectedValue)
        ? selectedValue
        : []
      : selectedValue
        ? [selectedValue]
        : []
    const keyedOptions = new Map()

    ;[...selected, ...options].forEach((option) => {
      const id = getAssignmentId(option)

      if (id && !keyedOptions.has(String(id))) {
        keyedOptions.set(String(id), option)
      }
    })

    return Array.from(keyedOptions.values())
  }

  const autocompleteValue = normaliseValueForAutocomplete(value)
  const autocompleteOptions = getOptionsWithSelectedValues(
    employeeOptions,
    autocompleteValue,
  )

  const commitChange = (event, newValue, reason, details) => {
    if (valueType === 'employee') {
      const toEmployeeValue = (v) => {
        if (!v) return null

        const employee = v.employee || v

        return {
          ...employee,
          userAccount: employee.userAccount || {
            _id: v._id,
            firstName: v.firstName,
            lastName: v.lastName,
            photograph: v.photograph,
          },
        }
      }

      const employeeValue = multiple
        ? newValue.map(toEmployeeValue).filter(Boolean)
        : toEmployeeValue(newValue)

      onChange(event, employeeValue, reason, details)
      setInputValue('')
      return
    }

    onChange(event, newValue, reason, details)
    setInputValue('')
  }

  const handleReactivate = async () => {
    if (!inactiveSelection || reactivating) return

    const { option, newValue, reason, details } = inactiveSelection

    setReactivating(true)
    try {
      // No days in the body: the server applies the tenant's configured window
      const res = await API.post(
        `/subcontractors/${option.subcontractor._id}/reactivate`,
      )

      if (res.status !== 200) {
        setToast({
          severity: 'error',
          message: 'Unable to reactivate subcontractor',
        })
        return
      }

      const updatedOption = {
        ...option,
        subcontractor: res.data,
        accessActive: true,
      }

      setEmployeeOptions((options) =>
        options.map((opt) => (opt === option ? updatedOption : opt)),
      )
      commitChange(
        null,
        multiple
          ? newValue.map((v) => (v === option ? updatedOption : v))
          : updatedOption,
        reason,
        details ? { ...details, option: updatedOption } : details,
      )
      setToast({
        message: `Subcontractor reactivated (${accessWindowLabel})`,
      })
      setInactiveSelection(null)
    } catch (err) {
      console.error(err)
      setToast({
        severity: 'error',
        message: 'Unable to reactivate subcontractor',
      })
    } finally {
      setReactivating(false)
    }
  }

  return (
    <>
    <Autocomplete
      fullWidth={fullWidth}
      size={size}
      options={autocompleteOptions}
      loading={loading}
      loadingText="Searching staff..."
      filterOptions={(x) => x}
      openOnFocus
      handleHomeEndKeys
      getOptionLabel={getOptionDisplayLabel}
      value={autocompleteValue}
      inputValue={multiple ? inputValue : undefined}
      renderTags={(options) => (
        <Box display="flex" gap={1} flexWrap="wrap">
          {options.map((opt) => {
            const { photograph } = getOptionDisplayParts(opt)
            return (
              <Box
                key={getAssignmentId(opt)}
                display="flex"
                gap={1}
                border="solid 1px rgba(0,0,0,0.24)"
                borderRadius={1}
                padding={1}
              >
                <Avatar src={photograph} sx={{ width: 24, height: 24 }} />
                <Typography>{getOptionDisplayLabel(opt)}</Typography>
                <SubcontractorIcon option={opt} />
              </Box>
            )
          })}
        </Box>
      )}
      renderOption={(props, option) => {
        const { key, ...rest } = props
        const { photograph } = getOptionDisplayParts(option)
        return (
          <Box
            key={key}
            display="flex"
            gap={1}
            alignItems="center"
            {...rest}
          >
            <Avatar src={photograph} sx={{ width: 24, height: 24 }} />
            <Typography>{getOptionDisplayLabel(option)}</Typography>
            <SubcontractorIcon option={option} />
            <SubcontractorStatusChip option={option} />
          </Box>
        )
      }}
      multiple={multiple}
      freeSolo={freeSolo}
      onChange={(event, newValue, reason, details) => {
        const added = multiple ? details?.option : newValue

        if (reason === 'selectOption' && isInactiveSubcontractor(added)) {
          setInactiveSelection({ option: added, newValue, reason, details })
          return
        }

        commitChange(event, newValue, reason, details)
      }}
      disabled={disabled}
      onInputChange={(event, newInputValue, reason) => {
        if (reason === 'input') {
          setInputValue(newInputValue)

          return
        }

        if (reason === 'clear') {
          setInputValue('')

          return
        }
      }}
      isOptionEqualToValue={(option, value) => {
        if (!option || !value) return false

        const optionAssignmentId = getAssignmentId(option)
        const valueAssignmentId = getAssignmentId(value)

        if (valueType === 'employee') {
          return String(optionAssignmentId) === String(valueAssignmentId)
        }

        return (
          String(optionAssignmentId) === String(valueAssignmentId) ||
          String(option._id) === String(value._id)
        )
      }}
      noOptionsText={
        inputValue.trim().length < 2
          ? 'Type at least 2 characters'
          : team
            ? `No staff found. Only employees in the ${team} team appear here.`
            : 'No staff found'
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={inputLabel}
          placeholder={multiple ? 'Type name...' : value ? '' : 'Type name...'}
          helperText={helperText}
          required={required}
          error={!!error}
          InputLabelProps={{
            shrink: !!shrinkLabel || !!value || !!params.inputProps?.value,
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                    <CircularProgress color="inherit" size={18} />
                  </Box>
                )}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: 'background.paper',
            },
          }}
        />
      )}
      {...props}
    />

    <Dialog
      open={!!inactiveSelection}
      onClose={() => !reactivating && setInactiveSelection(null)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Subcontractor Inactive</DialogTitle>
      <DialogContent>
        {inactiveSelection &&
          (canManageSubcontractors ? (
            <DialogContentText>
              {`${getOptionDisplayLabel(inactiveSelection.option)}'s subcontractor access has expired. You can reactivate them for ${accessWindowLabel} (as set in your company settings) and continue with this assignment.`}
            </DialogContentText>
          ) : (
            <DialogContentText>
              This subcontractor is inactive - please speak to your Personnel
              team.
            </DialogContentText>
          ))}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => setInactiveSelection(null)}
          disabled={reactivating}
        >
          {canManageSubcontractors ? 'Cancel' : 'Close'}
        </Button>
        {canManageSubcontractors && (
          <Button
            variant="contained"
            onClick={handleReactivate}
            disabled={reactivating}
            startIcon={
              reactivating ? (
                <CircularProgress color="inherit" size={16} />
              ) : null
            }
          >
            {accessWindowDays === 0
              ? 'Reactivate (indefinite)'
              : `Reactivate for ${accessWindowLabel}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
    </>
  )
}

export default EmployeeSelector
